import React, { useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { usePipeline } from '../PipelineContext';
import { dataService } from '../../../services/dataService';

const COLLECTION_INFO = {
  what: 'A structured data store — like a spreadsheet on the canvas. Agents read from and write to records as they process tasks.',
  examples: ['Support Tickets — status, assignee, priority per ticket', 'Dev Backlog — bugs and features with owners and progress'],
};

const STATUS_COLORS = {
  'Open':        '#3b82f6',
  'In Progress': '#f59e0b',
  'Resolved':    '#10b981',
  'Done':        '#10b981',
  'Escalated':   '#f43f5e',
  'Backlog':     '#94a3b8',
  'Review':      '#8b5cf6',
};

export default function CollectionNode({ id, data, selected }) {
  const [showInfo, setShowInfo] = useState(false);
  const { showTokens, tokenData, fmtTokens, fmtCost, opsMode, workspaceId } = usePipeline() || {};
  const nodeTokens  = tokenData?.[id];
  const accent      = data.color || '#0d9488';
  const headerStyle = data.color ? { background: data.color + '22', color: data.color } : {};
  const nodeStyle   = data.color ? { borderColor: data.color } : {};

  const [records, setRecords] = useState([]);
  const fields = data.fields || [];

  useEffect(() => {
    if (!workspaceId) return;
    return dataService.subscribeRecords(workspaceId, id, (recs) => {
      setRecords(recs);
    });
  }, [workspaceId, id]);

  const statusField = fields.find(f => f.type === 'select' && f.name.toLowerCase().includes('status'));
  const statusCounts = {};
  if (statusField) {
    records.forEach(r => {
      const v = r.values?.[statusField.id];
      if (v) statusCounts[v] = (statusCounts[v] || 0) + 1;
    });
  }

  return (
    <div data-testid="collection-node" className={`pipeline-node collection-node ${selected ? 'selected' : ''}`} style={nodeStyle}>
      <Handle type="target" position={Position.Left}  className="node-handle" />

      <div className="node-header collection-header" style={headerStyle}>
        <span className="node-type-icon">{opsMode ? '📁' : '⊞'}</span>
        <span className="node-type-label">{opsMode ? 'Records' : 'Collection'}</span>
        <button className="node-info-btn" onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); setShowInfo(s => !s); }}>ⓘ</button>
        <span className="node-agent-count" style={{ background: accent }}>
          {records.length}
        </span>
        {showTokens && nodeTokens && (
          <span className="token-total-badge" style={{ color: accent, background: accent + '18' }}>
            {opsMode ? fmtCost?.(nodeTokens.total) : `${fmtTokens(nodeTokens.total)} tok`}
          </span>
        )}
      </div>

      <div className="node-body">
        <div className="node-title">{data.label}</div>
        {data.description && <div className="node-description">{data.description}</div>}
        {showInfo && (
          <div className="node-info-card">
            <div className="node-info-what">{COLLECTION_INFO.what}</div>
            <div className="node-info-eg-label">Examples:</div>
            {COLLECTION_INFO.examples.map((ex, i) => <div key={i} className="node-info-eg">→ {ex}</div>)}
          </div>
        )}

        <div className="collection-meta">
          <span className="col-meta-pill">{fields.length} fields</span>
          <span className="col-meta-pill">{records.length} records</span>
        </div>

        {Object.keys(statusCounts).length > 0 && (
          <div className="collection-status-row">
            {Object.entries(statusCounts).map(([status, count]) => (
              <span
                key={status}
                className="col-status-chip"
                style={{ background: (STATUS_COLORS[status] || '#94a3b8') + '22', color: STATUS_COLORS[status] || '#94a3b8' }}
              >
                {status} {count}
              </span>
            ))}
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="node-handle" />
    </div>
  );
}
